import { CalendarClock, ArrowLeft, Plus, Info, Filter } from "lucide-react";
import { formatDate } from "@/lib/api";
import { FILTERS } from "./utils";

// Renders the top matter of the Bills page — the pay-period banner (when
// deep-linked from a Dashboard "Bills due in this period" link), the page
// title, the "review your bank statement" reminder, and the filter row.
// Extracted so `Bills.jsx` can stay focused on state + handlers.
export function BillsPageHeader({
  activePayPeriod,
  navigate,
  onAddBill,
  hasPendingClearing,
  filter,
  onFilterChange,
  accountFilter,
  onAccountFilterChange,
  fundingSources,
}) {
  return (
    <>
      {activePayPeriod && (
        <div className="rounded-2xl bg-[#F2EFEB] border border-[#D8CFB9] px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="bills-pay-period-banner">
          <div className="flex items-center gap-2 text-sm text-[#2C302E]">
            <CalendarClock size={16} className="text-[#4A6148]" />
            <span>
              Showing only bills for pay period{" "}
              <span className="font-semibold">
                {formatDate(activePayPeriod.period_start)} → {formatDate(activePayPeriod.period_end)}
              </span>
              {" · "}Paycheck arrives {formatDate(activePayPeriod.pay_date)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-ghost text-sm inline-flex items-center gap-1"
              onClick={() => navigate("/app/dashboard#your-next-pay-periods")}
              data-testid="bills-back-to-dashboard"
            >
              <ArrowLeft size={14} /> Back to Your Next Pay Periods
            </button>
            <button
              className="btn-ghost text-xs"
              onClick={() => navigate("/app/bills")}
              data-testid="bills-clear-pay-period-filter"
              title="Show all bills"
            >
              Clear filter
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Bills</div>
        <h1 className="font-heading text-3xl font-medium text-[#2C302E] mt-1">Your bill sheet</h1>
        <p className="text-[#59605C] mt-1 max-w-2xl">
          All bills for this and next month at a glance. Click any <span className="text-[#2C302E] font-medium">amount</span> to edit it inline, or click any <span className="text-[#2C302E] font-medium">due date</span> to reschedule that instance to a new date. Set each bill&rsquo;s <span className="text-[#2C302E] font-medium">Status</span> from the dropdown: <span className="text-[#59605C] font-medium">Unpaid</span>, <span className="text-[#8C3A30] font-medium">Pending</span> (paid, awaiting bank confirmation), or <span className="text-[#3B5B3E] font-medium">Paid</span> (cleared). Current pay period rows are tinted yellow; next pay period rows are tinted blue.
        </p>
        <button className="btn-primary mt-4" onClick={onAddBill} data-testid="bills-add-button">
          <Plus size={16} /> Add bill
        </button>
      </div>

      {hasPendingClearing && (
        <div className="rounded-2xl bg-[#FFF6ED] border border-[#F3E1CF] p-4 flex items-start gap-3" data-testid="bills-clearing-reminder">
          <Info size={18} className="text-[#8C5D30] mt-0.5 shrink-0" />
          <div className="text-sm text-[#8C5D30] leading-relaxed">
            <span className="font-medium">Review your bank statement.</span> Bills marked <span className="font-medium">Pending</span> have been paid (or auto-debit) but aren&rsquo;t confirmed yet. Change their status to <span className="font-medium">Paid</span> once you see them cleared in your account.
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap" data-testid="bills-filters">
        <Filter size={16} className="text-[#8A918D]" />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => onFilterChange(f.key)}
            data-testid={`bills-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.key ? "bg-[#4A6148] text-white" : "bg-[#F2EFEB] text-[#59605C] hover:bg-[#EBE6E0]"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <label htmlFor="bills-account-filter" className="text-xs font-medium text-[#8A918D] uppercase tracking-widest">
            Search
          </label>
          <select
            id="bills-account-filter"
            data-testid="bills-account-filter"
            value={accountFilter}
            onChange={(e) => onAccountFilterChange(e.target.value)}
            className="input-soft !py-1.5 !px-3 !text-sm max-w-[220px]"
          >
            <option value="all">By Spending Account</option>
            <option value="__unassigned__">Unassigned (no account)</option>
            {fundingSources.map((fs) => (
              <option key={fs.id} value={fs.id}>{fs.name}</option>
            ))}
          </select>
          {accountFilter !== "all" && (
            <button
              type="button"
              onClick={() => onAccountFilterChange("all")}
              className="text-xs text-[#8A918D] hover:text-[#2C302E]"
              data-testid="bills-account-filter-clear"
              title="Clear account filter"
            >
              clear
            </button>
          )}
        </div>
      </div>
    </>
  );
}
